import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProgressIndicator from './components/ProgressIndicator';
import TrustSignals from './components/TrustSignals';
import PersonalInfoStep from './components/PersonalInfoStep';
import SecurityStep from './components/SecurityStep';
import PrivacyPreferencesStep from './components/PrivacyPreferencesStep';
import PartnerVerificationStep from './components/PartnerVerificationStep';
import ConsentStep from './components/ConsentStep';

const PartnerOnboarding = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    relationshipStatus: '',
    relationshipDuration: '',
    password: '',
    confirmPassword: '',
    securityQuestion: '',
    securityAnswer: '',
    shareMoodData: true,
    shareAvailability: true,
    sharePreferences: true,
    enableAIMediation: true,
    allowCompatibilityAnalysis: true,
    receiveAISuggestions: true,
    notifyMoodChanges: true,
    notifyScheduleReminders: true,
    notifyAIMessages: true,
    partnerEmail: '',
    verificationCode: '',
    myVerificationCode: Math.random()?.toString(36)?.substring(2, 8)?.toUpperCase(),
    acceptTerms: false,
    acceptPrivacy: false,
    consentAICommunication: false,
    confirmAge: false,
    emailConsent: false
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'security', label: 'Security' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'verification', label: 'Verification' },
    { id: 'consent', label: 'Consent' }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!formData?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
      if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!formData?.gender) newErrors.gender = 'Please select your gender';
      if (!formData?.relationshipStatus) newErrors.relationshipStatus = 'Please select your relationship status';
    }

    if (currentStep === 2) {
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (!formData?.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData?.securityQuestion?.trim()) newErrors.securityQuestion = 'Security question is required';
      if (!formData?.securityAnswer?.trim()) newErrors.securityAnswer = 'Security answer is required';
    }

    if (currentStep === 4) {
      if (!formData?.partnerEmail?.trim() && !formData?.verificationCode?.trim()) {
        newErrors.partnerEmail = 'Please provide partner email or verification code';
      }
    }

    if (currentStep === 5) {
      if (!formData?.acceptTerms) newErrors.acceptTerms = 'You must accept the Terms of Service';
      if (!formData?.acceptPrivacy) newErrors.acceptPrivacy = 'You must accept the Privacy Policy';
      if (!formData?.consentAICommunication) newErrors.consentAICommunication = 'AI communication consent is required';
      if (!formData?.confirmAge) newErrors.confirmAge = 'You must confirm you meet the age requirement';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < steps?.length) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSaveAndExit = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Progress saved! You can continue later from where you left off.');
    }, 1000);
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      setIsSaving(true);
      setSubmitError('');
      
      try {
        const { data, error } = await signUp(formData?.email, formData?.password, {
          firstName: formData?.firstName,
          lastName: formData?.lastName,
          dateOfBirth: formData?.dateOfBirth,
          gender: formData?.gender,
          relationshipStatus: formData?.relationshipStatus,
          relationshipDuration: formData?.relationshipDuration,
          shareMoodData: formData?.shareMoodData,
          shareAvailability: formData?.shareAvailability,
          sharePreferences: formData?.sharePreferences,
          enableAIMediation: formData?.enableAIMediation,
          allowCompatibilityAnalysis: formData?.allowCompatibilityAnalysis,
          receiveAISuggestions: formData?.receiveAISuggestions,
          notifyMoodChanges: formData?.notifyMoodChanges,
          notifyScheduleReminders: formData?.notifyScheduleReminders,
          notifyAIMessages: formData?.notifyAIMessages
        });

        if (error) {
          setSubmitError(error?.message || 'Failed to create account. Please try again.');
          setIsSaving(false);
          return;
        }

        // Success - navigate to dashboard
        navigate('/shared-profile-dashboard');
      } catch (error) {
        setSubmitError('An unexpected error occurred. Please try again.');
        setIsSaving(false);
      }
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <PersonalInfoStep formData={formData} errors={errors} onChange={handleChange} />;
      case 2:
        return <SecurityStep formData={formData} errors={errors} onChange={handleChange} />;
      case 3:
        return <PrivacyPreferencesStep formData={formData} onChange={handleChange} />;
      case 4:
        return <PartnerVerificationStep formData={formData} errors={errors} onChange={handleChange} />;
      case 5:
        return <ConsentStep formData={formData} errors={errors} onChange={handleChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <div className="flex items-center justify-between mb-6 md:mb-8 lg:mb-10">
          <div className="flex items-center space-x-2 md:space-x-3">
            <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md">
              <Icon name="Heart" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                LoveNudge
              </h1>
              <p className="text-xs md:text-sm text-muted-foreground">
                Partner Onboarding
              </p>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            iconName="Save"
            iconPosition="left"
            onClick={handleSaveAndExit}
            loading={isSaving}
          >
            <span className="hidden md:inline">Save & Exit</span>
            <span className="md:hidden">Save</span>
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 shadow-therapeutic">
              <ProgressIndicator
                currentStep={currentStep}
                totalSteps={steps?.length}
                steps={steps}
              />

              <div className="mt-6 md:mt-8">
                {renderStepContent()}
              </div>

              <div className="flex flex-col-reverse md:flex-row items-stretch md:items-center justify-between gap-3 md:gap-4 mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border">
                {submitError && (
                  <div className="w-full bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
                    <p className="text-sm text-destructive">{submitError}</p>
                  </div>
                )}
                <Button
                  variant="outline"
                  iconName="ChevronLeft"
                  iconPosition="left"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  fullWidth
                  className="md:w-auto"
                >
                  Back
                </Button>

                {currentStep < steps?.length ? (
                  <Button
                    variant="default"
                    iconName="ChevronRight"
                    iconPosition="right"
                    onClick={handleNext}
                    fullWidth
                    className="md:w-auto"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleSubmit}
                    loading={isSaving}
                    fullWidth
                    className="md:w-auto"
                  >
                    Complete Registration
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <TrustSignals />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerOnboarding;